/**
 * DOM operations that collapse an email's quoted-reply trailer behind a toggle.
 *
 * React-free and self-contained so it can run in the parent realm against a
 * same-origin srcDoc iframe document (no `allow-scripts` needed — same trust
 * model as the link interceptor) AND be unit-tested with a plain `Document`
 * (jsdom). The pure boundary decision lives in {@link isQuotedReplyBoundary}.
 */

import {
  isQuotedReplyBoundary,
  type QuoteBoundaryDescriptor,
} from "./quote-detection.js";

export const QUOTE_SHOW_LABEL = "Show quoted text";
export const QUOTE_HIDE_LABEL = "Hide quoted text";

const PROCESSED_ATTR = "data-quote-processed";
const TOGGLE_ATTR = "data-quote-toggle";
const CONTENT_ATTR = "data-quote-content";

function describe(el: Element): QuoteBoundaryDescriptor {
  return {
    tagName: el.tagName.toLowerCase(),
    id: el.getAttribute("id") ?? "",
    className: el.getAttribute("class") ?? "",
    text: (el.textContent ?? "").replace(/\s+/g, " ").trim(),
  };
}

/**
 * Find, within `doc.body`'s top-level children, the first element that begins
 * the quoted / forwarded trailer AND is preceded by visible new content. Wrap
 * that element and everything after it in a hidden container, and insert a
 * toggle button before it. Idempotent (guards on `data-quote-processed`).
 *
 * Returns `true` when a trailer was collapsed, `false` otherwise (no boundary,
 * or the body is quote-only so collapsing would hide everything).
 */
export function collapseQuotedTrailer(doc: Document): boolean {
  const body = doc.body;
  if (!body || body.hasAttribute(PROCESSED_ATTR)) return false;
  body.setAttribute(PROCESSED_ATTR, "");

  const children = Array.from(body.children);
  let boundaryIndex = -1;
  for (let i = 0; i < children.length; i++) {
    if (isQuotedReplyBoundary(describe(children[i]))) {
      boundaryIndex = i;
      break;
    }
  }
  if (boundaryIndex < 0) return false;

  // Require visible new content above the boundary. Without it the body is
  // quote-only (a thread's first message, or a bare forward with no lead-in) —
  // collapsing would hide everything, so leave it expanded.
  const hasVisibleBefore = children.slice(0, boundaryIndex).some((el) => {
    return (el.textContent ?? "").trim().length > 0 || el.querySelector("img,table") != null;
  });
  if (!hasVisibleBefore) return false;

  const toggle = doc.createElement("button");
  toggle.setAttribute("type", "button");
  toggle.setAttribute(TOGGLE_ATTR, "");
  toggle.setAttribute("aria-expanded", "false");
  toggle.setAttribute("aria-label", QUOTE_SHOW_LABEL);
  toggle.setAttribute("title", QUOTE_SHOW_LABEL);
  toggle.textContent = "•••";

  const content = doc.createElement("div");
  content.setAttribute(CONTENT_ATTR, "");
  content.setAttribute("hidden", "");

  const boundaryEl = children[boundaryIndex];
  body.insertBefore(toggle, boundaryEl);
  body.insertBefore(content, boundaryEl);
  // `children` was captured before insertion, so it holds only original nodes;
  // moving each into `content` detaches it from `body` in order.
  for (let i = boundaryIndex; i < children.length; i++) {
    content.appendChild(children[i]);
  }
  return true;
}

/**
 * Flip the collapsed quoted region's visibility, syncing the toggle's
 * `aria-expanded` / label. Returns the new state, or `null` if there is no
 * collapsed region in the document.
 */
export function toggleQuoteRegion(doc: Document): "shown" | "hidden" | null {
  const toggle = doc.querySelector(`[${TOGGLE_ATTR}]`);
  const content = doc.querySelector(`[${CONTENT_ATTR}]`);
  if (!toggle || !content) return null;

  const willShow = content.hasAttribute("hidden");
  if (willShow) content.removeAttribute("hidden");
  else content.setAttribute("hidden", "");

  toggle.setAttribute("aria-expanded", willShow ? "true" : "false");
  const label = willShow ? QUOTE_HIDE_LABEL : QUOTE_SHOW_LABEL;
  toggle.setAttribute("aria-label", label);
  toggle.setAttribute("title", label);
  return willShow ? "shown" : "hidden";
}
